import React, { useRef, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { KIND_LABELS, DOC_PATH } from "./BillAttachments";

export const ATTACHMENT_KINDS = [
  { value: "auto", label: "Automatisch erkennen" },
  { value: "payment_receipt", label: "Zahlungsbeleg" },
  { value: "delivery_note", label: "Lieferschein" },
  { value: "reminder", label: "Mahnung" },
  { value: "terms", label: "AGB" },
  { value: "quote", label: "Offerte" },
  { value: "contract", label: "Vertrag" },
  { value: "other", label: "Sonstige" },
];

export function BillAttachmentUploadDialog({ bill, onClose, onUploaded, docType = "bill" }) {
  const [kind, setKind] = useState("auto");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!files.length) return toast.error("Bitte mindestens eine Datei wählen");
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("kind", kind);
      const r = await apiClient.post(`/${DOC_PATH[docType]}/${bill._id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const pi = r.data.payment_info;
      const dc = r.data.delivery_check;
      const kinds = [...new Set((r.data.added || []).map((a) => KIND_LABELS[a.kind] || a.kind))].join(", ");
      toast.success(
        `${r.data.added.length} Beilage${r.data.added.length > 1 ? "n" : ""} angehängt${kinds ? ` (${kinds})` : ""}` +
          (pi?.charged_amount_chf ? ` · belastet CHF ${pi.charged_amount_chf.toFixed(2)}` : "") +
          (pi?.paid_date ? ` · bezahlt am ${formatDate(pi.paid_date)}` : "") +
          (dc?.message ? ` · ${dc.message}` : "")
      );
      onUploaded(r.data.doc);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!bill} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="bill-attachment-upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">Beilage nachreichen · {bill?.bill_number || bill?.description}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Art der Beilage</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {ATTACHMENT_KINDS.map((k) => (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => setKind(k.value)}
                  className={`rounded-full px-3 py-1 text-xs border ${kind === k.value ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                  data-testid={`bill-attachment-kind-${k.value}`}
                >
                  {k.label}
                </button>
              ))}
            </div>
            {kind === "payment_receipt" && (
              <p className="mt-1.5 text-[11px] text-slate-500">Bezahldatum, belasteter CHF-Betrag, Kurs und Karte werden automatisch ausgelesen.</p>
            )}
            {kind === "auto" && (
              <p className="mt-1.5 text-[11px] text-slate-500">Die Art (Zahlungsbeleg, Lieferschein, Mahnung, AGB …) wird per Regeln/KI erkannt; Lieferscheine werden automatisch mit der Rechnung abgeglichen.</p>
            )}
            {kind === "delivery_note" && (
              <p className="mt-1.5 text-[11px] text-slate-500">Positionen und Mengen werden automatisch mit der Rechnung abgeglichen.</p>
            )}
          </div>
          <div>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="application/pdf,image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files || [])])}
              data-testid="bill-attachment-file-input"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="w-full rounded-lg border-2 border-dashed border-slate-300 px-4 py-6 text-center text-xs text-slate-600 hover:border-violet-400 hover:bg-violet-50/40"
              data-testid="bill-attachment-dropzone"
            >
              <Upload className="mx-auto mb-1.5 h-5 w-5 text-slate-400" />
              Dateien wählen (PDF, JPG, PNG · max. 15 MB, mehrere möglich)
            </button>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="bill-attachment-file-list">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center justify-between rounded bg-slate-50 px-2 py-1 text-xs">
                    <span className="truncate">{f.name}</span>
                    <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`bill-attachment-file-remove-${i}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={busy}>Abbrechen</Button>
            <Button type="button" size="sm" onClick={submit} disabled={busy || !files.length} data-testid="bill-attachment-upload-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
              Hochladen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
